<script lang="ts" setup>
import { computed, nextTick, onMounted, ref, watch } from "vue"
import { Head, useForm, usePage } from "@inertiajs/vue3"
import AuthenticationCard from "@/Components/AuthenticationCard.vue"
import TurnstileWidget from "@/Components/TurnstileWidget.vue"
import usePasskeySignIn from "@/Compositions/usePasskeySignIn"

const props = defineProps<{
    canResetPassword?: boolean
    status?: string | null
    passkeyBrowser?: boolean
}>()

const page = usePage<{ turnstile: { siteKey: string | null } }>()
const query = new URLSearchParams(page.url.split("?")[1] ?? "")
const siteKey = computed(() => page.props.turnstile.siteKey)

const form = useForm({
    email: query.get("email") ?? "",
    password: "",
    shared_device: false,
    "cf-turnstile-response": "",
})

const passkey = usePasskeySignIn(() => form.shared_device)

const showPassword = ref(query.get("password") === "1")
const passwordInput = ref<{ inputRef: HTMLInputElement } | null>(null)
const turnstile = ref<InstanceType<typeof TurnstileWidget> | null>(null)

const googleUrl = computed(() =>
    route("auth.redirect", { provider: "google", ...(form.shared_device ? { shared_device: 1 } : {}) }),
)

const securityCheckPending = computed(() => siteKey.value !== null && form["cf-turnstile-response"] === "")
const waitingForSecurityCheck = computed(() => securityCheckPending.value && form.password === "")

watch(
    () => form.password,
    (password) => {
        if (password !== "") {
            showPassword.value = true
        }
    },
)

const revealPassword = async () => {
    showPassword.value = true

    await nextTick()

    passwordInput.value?.inputRef.focus()
}

const sendCode = () => {
    form.transform((data) => ({
        email: data.email,
        shared_device: data.shared_device,
        "cf-turnstile-response": data["cf-turnstile-response"],
    })).post(route("sign-in.code.store"), {
        onFinish: () => turnstile.value?.reset(),
    })
}

const signInWithPassword = () => {
    form.transform((data) => ({
        email: data.email,
        password: data.password,
        shared_device: data.shared_device,
    })).post(route("login"), {
        onFinish: () => form.reset("password"),
    })
}

const submit = async () => {
    if (form.password !== "") {
        signInWithPassword()

        return
    }

    if (passkey.pending.value) {
        return
    }

    if (props.passkeyBrowser && passkey.supported.value && (await passkey.signIn())) {
        return
    }

    sendCode()
}

onMounted(passkey.armAutofill)
</script>

<template>
    <Head title="Log in" />

    <AuthenticationCard>
        <template #title>Welcome to GetCopy</template>

        <template #description>Sign in or create your account</template>

        <template #header>
            <UButton
                :to="googleUrl"
                icon="i-flat-color-icons-google"
                color="neutral"
                variant="outline"
                block
                class="w-full mt-8"
                label="Continue with Google" />

            <USeparator
                class="mt-4"
                label="OR" />
        </template>

        <div
            v-if="status"
            class="mb-4 font-medium text-sm text-gray-700 dark:text-gray-300">
            {{ status }}
        </div>

        <form @submit.prevent="submit">
            <UFormField
                label="Email"
                :error="form.errors.email">
                <UInput
                    id="email"
                    v-model="form.email"
                    type="email"
                    class="w-full"
                    required
                    autofocus
                    autocomplete="username webauthn" />
            </UFormField>

            <div
                :class="showPassword ? 'mt-4' : 'sr-only'"
                :aria-hidden="showPassword ? undefined : 'true'">
                <UFormField
                    label="Password"
                    :error="form.errors.password">
                    <UInput
                        id="password"
                        ref="passwordInput"
                        v-model="form.password"
                        type="password"
                        class="w-full"
                        :tabindex="showPassword ? undefined : -1"
                        autocomplete="current-password" />
                </UFormField>
            </div>

            <div class="flex justify-end mt-2">
                <UButton
                    v-if="!showPassword"
                    type="button"
                    color="neutral"
                    variant="link"
                    size="xs"
                    label="Sign in with password"
                    @click="revealPassword" />

                <UButton
                    v-else
                    type="button"
                    color="neutral"
                    variant="link"
                    size="xs"
                    label="Forgot your password? Get a sign-in code instead"
                    :disabled="securityCheckPending || form.processing"
                    @click="sendCode" />
            </div>

            <div class="block mt-4">
                <UCheckbox
                    v-model="form.shared_device"
                    name="shared_device"
                    label="Shared computer? Don't keep me signed in"
                    :ui="{ label: 'text-sm text-gray-600 dark:text-gray-300 dark:text-white' }" />
            </div>

            <UFormField
                v-if="siteKey"
                class="mt-4"
                :error="form.errors['cf-turnstile-response']">
                <TurnstileWidget
                    ref="turnstile"
                    :site-key="siteKey"
                    @update:token="form['cf-turnstile-response'] = $event ?? ''" />
            </UFormField>

            <UButton
                type="submit"
                class="mt-4"
                block
                :loading="form.processing || passkey.pending.value"
                :disabled="waitingForSecurityCheck">
                Continue
            </UButton>

            <p class="mt-3 text-center text-sm text-gray-700 dark:text-gray-300">
                By continuing, you agree to the
                <a
                    :href="route('terms.show')"
                    target="_blank"
                    rel="noopener"
                    class="underline font-medium hover:text-gray-900 dark:hover:text-white"
                    >Terms</a
                >
                and
                <a
                    :href="route('policy.show')"
                    target="_blank"
                    rel="noopener"
                    class="underline font-medium hover:text-gray-900 dark:hover:text-white"
                    >Privacy Policy</a
                >
            </p>
        </form>
    </AuthenticationCard>
</template>
